/**
 * Member Payments — dues status, the four numbers that matter, and every
 * receipt they have ever been issued.
 *
 * The CTA follows the same rule as everywhere: only when money is owed.
 */

import { duesState, formatDate, formatMoney, TIERS } from '../../data';
import { useClubData } from '../../context/ClubContext';
import { useUI } from '../../context/UIContext';
import { DuesBar } from '../../components/DuesBar';
import { SelfPaySheet } from '../../components/sheets/MoneySheets';
import { ProfileSheet } from '../../components/sheets/ProfileSheet';
import { Button, Empty, Eyebrow, Fact, Stat, Stats, Table, ViewTitle } from '../../components/ui';
import { useGrounds } from '../../context/ClubContext';

export function PaymentsPage() {
  const grounds = useGrounds();
  const { db, me } = useClubData();
  const { openSheet } = useUI();

  if (!me) return <Empty title="No member">Sign in again to see your payments.</Empty>;

  const tier = TIERS[me.tier];
  const dues = duesState(me);
  const isAlert = dues.key === 'lapsed' || dues.key === 'pending';

  const mine = db.payments
    .filter((p) => p.memberId === me.id)
    .sort((a, b) => b.date.localeCompare(a.date));
  const paidToDate = mine.reduce((sum, p) => sum + p.amount, 0);

  return (
    <>
      <ViewTitle title="Payments" sub="Your dues, due date and every receipt" />

      <DuesBar
        member={me}
        variant="payments"
        onPay={() => openSheet(<SelfPaySheet member={me} />)}
      />

      <Stats>
        <Stat value={`₹${tier.fee}`} label={`${tier.label} fee`} />
        <Stat
          tone={isAlert ? 'bad' : 'good'}
          value={
            me.tier === 'life' ? (
              '—'
            ) : (
              /* a date is words, not a figure: at the stat size it broke into
                 "31 Mar / 27" in a phone-width cell */
              <span className="whitespace-nowrap text-[24px] sm:text-[32px]">
                {formatDate(me.duesPaidTill)}
              </span>
            )
          }
          label="Paid until"
        />
        <Stat
          tone="good"
          numberTone="good"
          value={formatMoney(paidToDate)}
          label="Paid to date"
        />
        <Stat value={mine.length} label="Receipts" />
      </Stats>

      {/* Billing contact. A pay link is sent by WhatsApp to the number on the
          roster, so a stale mobile is the usual reason a member never gets
          one — the fix belongs here, not two screens away. Same sheet as the
          profile page: one form, one save path. */}
      <Eyebrow>Your details</Eyebrow>
      <div className="mb-3.5 border border-line bg-white p-4">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2.5">
          <div className="font-market text-[9.5px] uppercase tracking-[0.13em] text-copy-muted">
            Where we send your pay link
          </div>
          <Button tone="p" small onClick={() => openSheet(<ProfileSheet member={me} />)}>
            Edit details
          </Button>
        </div>
        <div className="grid grid-cols-2 gap-x-4 gap-y-3.5 min-[560px]:grid-cols-3">
          <Fact k="Mobile" v={me.phone} mono />
          <Fact k="Membership" v={tier.label} />
          <Fact k="Home ground" v={grounds[me.ground] ?? ''} />
        </div>
      </div>

      <Eyebrow>Payment history</Eyebrow>
      {mine.length ? (
        <Table
          head={
            <tr>
              <th>Receipt</th>
              <th>Date</th>
              <th>Amount</th>
              <th>Method</th>
              <th>Covers till</th>
            </tr>
          }
        >
          {mine.map((p) => (
            <tr key={p.id}>
              <td>
                {p.receipt}
                {/* Their own screenshot, kept with the receipt — this is the
                    copy a member goes looking for months later. */}
                {p.proofUrl && (
                  <a
                    href={p.proofUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-0.5 block font-market text-[10px] text-gold-800 underline underline-offset-2"
                  >
                    Screenshot
                  </a>
                )}
              </td>
              <td>{formatDate(p.date)}</td>
              <td>
                <b>{formatMoney(p.amount)}</b>
              </td>
              <td>{p.method.toUpperCase()}</td>
              <td>{p.coversTill ? formatDate(p.coversTill) : '—'}</td>
            </tr>
          ))}
        </Table>
      ) : (
        <Empty title="No payments yet">Your first receipt will show here.</Empty>
      )}
    </>
  );
}
